import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { LoadingMask } from "../../LoadingMask";
import { toast, ToastContainer } from "react-toastify";
import { AgencyMailTypeModal } from "./AgencyMailTypeModal";
import { DeleteAgencyMailTypeModal } from "./DeleteAgencyMailTypeModal";
import { MailType } from "../../../models/Mail";
import Button from "../../Button";
import { PlusIcon } from "@heroicons/react/24/outline";
import Table from "../../Table";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "../../Toast";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { deleteAgencyMailType } from "../../../state/mail/actions";
import { Agency } from "../../../models/Agency";

interface Props {
    currentAgency: Agency
}

export const AgencyMailTypeModule = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [isMailTypeLoading, setIsMailTypeLoading] = useState(false);
    const agencyMailTypes = useSelector((state: RootState) => state.mails.agencyMailTypes);
    const agencyMailTypeError = useSelector((state: any) => state.mails.error);
    const [showMailTypeError, setShowMailTypeError] = useState(false);

    const [showAgencyMailTypeModal, setShowAgencyMailTypeModal] = useState<{
        show: boolean;
        update: boolean;
        type?: MailType;
        index?: number;
    }>({ show: false, update: false });

    const [ showDeleteAgencyMailTypeModal, setShowDeleteAgencyMailTypeModal] = useState<{
        type?: MailType;
        show: boolean;
    }>({ show: false, type: undefined });

    useEffect(() => {
        if (showMailTypeError) {
            Toast(t(agencyMailTypeError?.reason ?? "SOMETHING_WENT_WRONG"), t(agencyMailTypeError?.cause_info));
            setShowMailTypeError(false);
        }
    }, [showMailTypeError, agencyMailTypeError, t]);

    const getMailTypesRows = useMemo(() => {
        return agencyMailTypes?.map((type: MailType) => {
            return {
                columns: [
                { value: type.code, className: "w-1/3" },
                { value: type.name, className: "w-1/3" },
                {
                    value: (
                    <div className="flex flex-row justify-end divide-x">
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("UPDATE_MAIL_TYPE")}
                            onClick={() => setShowAgencyMailTypeModal({ show: true, update: true, type: type })}
                            classNames="mr-2">
                                <PencilIcon className="h-5 aspect-square text-blue" />
                        </Button>
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("DELETE_MAIL_TYPE")}
                            onClick={() => setShowDeleteAgencyMailTypeModal({ show: true, type: type })}
                            classNames="mx-2"
                            >
                                <TrashIcon className="h-5 aspect-square text-red-600" />
                        </Button>
                    </div>
                    ), className: "w-1/3"
                },
                ],
            };
        });
    }, [agencyMailTypes, t]);

    const onDeleteAgencyMailType = (type: MailType) => {
        setIsMailTypeLoading(true)
        dispatch(deleteAgencyMailType({agencyId: props.currentAgency.id, typeId: type.id!})).then((e) => {
            if (e.type === "mail/deleteAgencyMailType/rejected") {
                setIsMailTypeLoading(false)
                setShowMailTypeError(true);
            } else {
                setIsMailTypeLoading(false)
                toast(t("SUCCESSFULLY_DELETE_AGENCY_MAIL_TYPE"));
            }
        });
    };

    return(
        <>
            {isMailTypeLoading && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <AgencyMailTypeModal
                info={showAgencyMailTypeModal}
                onClose={() => setShowAgencyMailTypeModal({ show: false, update: false, type: undefined })}
            />
            <DeleteAgencyMailTypeModal 
                info={showDeleteAgencyMailTypeModal}
                onClose={() => setShowDeleteAgencyMailTypeModal({ show: false, type: undefined })}
                deleteType={(type: MailType) => onDeleteAgencyMailType(type)}
            />
            <div className="text-black max-h-full p-5 md:w-full">
                <div className="flex justify-between w-full">
                    <div className="text-md font-bold text-black pl-3 mt-2">
                        {t("PHYSICAL_MAIL_TYPES")}
                    </div>
                    <Button onClick={() => setShowAgencyMailTypeModal({ show: true, update: false })}>
                        <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2">
                                <PlusIcon />
                            </div>
                            {t("ADD_MAIL_TYPE")}
                        </span>
                    </Button>
                </div>
                <div className="w-full my-4">
                    <Table
                        columns={[
                            { header: `${t("CODE")}` },
                            { header: `${t("NAME")}` },
                            { header: "" },
                        ]}
                        data={getMailTypesRows}
                    />
                </div>
            </div>
        </>
    )
}